import {
  AvailabilityRuleTypeDto,
  DayOfWeekDto,
  OfferElementTypeDto,
  OfferTypeDto,
  RecurrenceFrequencyDto,
} from '@/api/generated';
import {
  DishCategoryOptionsTranslation,
  dishCategoryOptionsTranslation,
  ProcessOfferStatusOptionsTranslation,
  processOfferStatusOptionsTranslation,
} from '@/assets/locales/utils/dictionaries/dictionaries.translation.pl.ts';
import {
  OfferElementTypeOptions,
  offerElementTypeOptions,
} from '@/assets/locales/views/offerElements/offerElements.common.translation.pl.ts';
import {
  processOfferDishesTranslation,
  ProcessOfferDishesTranslation,
} from '@/assets/locales/views/processes/offer/processOffer.dishes.translation.pl.ts';
import {
  processOfferScheduleTranslation,
  ProcessOfferScheduleTranslation,
} from '@/assets/locales/views/processes/offer/schedule/offerSchedule.translation.pl.ts';
import { PolishPluralForm } from '@/views/processes/offer/components/aiChat/util/offerAiQuestion.util.ts';
import { OfferWizardStepEnum } from '@/views/processes/offer/components/checklist/model/OfferWizardStep.enum.ts';
import { ProcessOfferDialogModel } from '@/views/processes/offer/components/dialog/model/ProcessOfferDialog.model.ts';
import { ProcessOfferSelectRealizationContactDialogModel } from '@/views/processes/offer/components/dialog/selectRealizationContactDialog/model/ProcessOfferSelectRealizationContactDialog.model.ts';
import { ProcessOfferGroupModalModel } from '@/views/processes/offer/components/groups/modal/ProcessOfferGroupModal.model.ts';
import { ProcessOfferSubgroupModalModel } from '@/views/processes/offer/components/groups/modal/ProcessOfferSubgroupModal.model.ts';
import { ProcessOfferCreateModel } from '@/views/processes/offer/model/ProcessOfferCreate.model.ts';

import {
  processOfferElementsTranslation,
  ProcessOfferElementsTranslation,
} from './elements/processOffer.elements.translation.pl';
import {
  processOfferEmailsTranslation,
  ProcessOfferEmailsTranslation,
} from './emails/processOfferEmails.translation.pl';
import {
  processOfferSectionsTranslation,
  ProcessOfferSectionsTranslation,
} from './sections/processOffer.packagesSection.translation.pl';

type DeleteModalTranslation = {
  button: {
    cancel: string;
    confirm: string;
  };
  content: string;
  title: string;
};

const overriddenPrice = 'Cena sprzedawcy';
const noOfGuests = 'Liczba gości';
const eventTime = 'Czas wydarzenia';

const deleteModalTranslation: DeleteModalTranslation = {
  button: {
    cancel: 'Anuluj',
    confirm: 'Usuń',
  },
  content: 'Potwierdź usunięcie: ',
  title: 'Potwierdź usunięcie',
};

export type ProcessOfferTranslation = {
  aiChat: {
    authorAssistant: string;
    authorUser: string;
    changeLog: {
      entryNotUndoable: string;
      outsideOfferHint: string;
      showAll: string;
      showLess: string;
      title: string;
      titleUndone: string;
      undo: string;
    };
    close: string;
    composer: {
      hint: string;
      morePrompts: string;
      morePromptsHint: string;
      morePromptsTitle: string;
      placeholder: string;
      send: string;
    };
    copy: string;
    copySuccess: string;
    empty: {
      description: string;
      suggestionsTitle: string;
      title: string;
    };
    proposal: {
      apply: string;
      clear: string;
      decrease: string;
      dismiss: string;
      followsOffer: {
        HOUR: string;
        PEOPLE: string;
      };
      increase: string;
      individualPrice: string;
      itemsCount: string;
      nothingAdded: string;
      pieces: string;
      pieceUnit: string;
      quantity: string;
      selectAll: string;
      setGuestCount: string;
      status: {
        APPLIED: string;
        DISMISSED: string;
        PENDING: string;
      };
      title: string;
      total: string;
      unitPrice: {
        HOUR: string;
        OTHER: string;
        PEOPLE: string;
        UNIT: string;
      };
    };
    question: {
      dishes: Record<PolishPluralForm, string>;
      dishSources: {
        add: string;
        allDepartments: string;
        allDishTypes: string;
        answered: string;
        backToEventType: string;
        change: string;
        count: string;
        decrease: string;
        departments: string;
        dishTypes: string;
        done: string;
        increase: string;
        matching: string;
        matchingFewer: string;
        narrowed: string;
        narrowingWithoutDishes: string;
        noMatching: string;
        showWholeCatalog: string;
        skippedSubgroup: string;
        title: string;
        wholeCatalog: string;
      };
      dishUnit: Record<PolishPluralForm, string>;
      skip: string;
      skipped: string;
      subgroups: {
        answered: string;
        clear: string;
        empty: string;
        filled: string;
        hideFilled: string;
        next: string;
        selectEmpty: string;
        showFilled: string;
        title: string;
      };
      userMessage: {
        dishSources: string;
        subgroups: string;
        wholeCatalog: string;
      };
    };
    reportTooltip: string;
    subtitleGuests: string;
    suggestedReplies: string;
    suggestions: Record<
      'equipment' | 'fillDishes' | 'notesAndEmails' | 'offerEmail' | 'review' | 'summary',
      { hint: string; label: string; prompt: string }
    >;
    thinking: string;
    thinkingLong: string;
    title: string;
    today: string;
    yesterday: string;
  };
  availability: {
    button: {
      addRule: string;
      removeRule: string;
    };
    day: Record<DayOfWeekDto, string>;
    emptyHint: string;
    field: {
      dayOfMonth: string;
      daysOfWeek: string;
      frequency: string;
      intervalValue: string;
      ruleType: string;
      specificDate: string;
      validFrom: string;
      validTo: string;
    };
    frequency: Record<RecurrenceFrequencyDto, string>;
    ruleType: Record<AvailabilityRuleTypeDto, string>;
    summary: {
      always: string;
      draft: string;
      intervalEvery: string;
      monthDay: string;
      on: string;
      rangeBoth: string;
      rangeFrom: string;
      rangeTo: string;
      weeklyDays: string;
    };
    title: string;
  };
  calendar: {
    title: string;
  };
  checklist: {
    allClear: string;
    button: {
      add: string;
      back: string;
      change: string;
      close: string;
      continue: string;
      distribute: string;
      explain: string;
      show: string;
    };
    items: {
      discountAwaitingApproval: string;
      discountRejected: string;
      dishesOutOfSeason: string;
      dishesPriceOutOfTerm: string;
      emptyGroup: string;
      emptySubgroup: string;
      groupDatesWithoutOfferDates: string;
      groupGuestsDiffer: string;
      groupOutsideOfferDates: string;
      noCustomer: string;
      noElement: OfferElementTypeOptions;
      noGroups: string;
      noGuests: string;
      noLocation: string;
      noRealizationContact: string;
      pastEvent: string;
      priceBelowMinimum: string;
      subgroupRules: string;
    };
    nonBlocking: string;
    skipped: {
      reason: string;
      title: string;
    };
    subgroupIssues: {
      belowBudget: string;
      belowOrderMinimum: string;
      distributionOutdated: string;
      distributionOutdatedDishes: string;
      incompletePick: string;
      minimumsOverBudget: string;
      overBudget: string;
      tooFewDistinct: string;
      unitMismatch: string;
    };
    summary: string;
    title: string;
    wizard: {
      button: {
        goToStep: string;
        list: string;
        next: string;
        prev: string;
        save: string;
        steps: string;
      };
      customer: {
        current: string;
        goToProcess: string;
        hint: string;
      };
      elements: {
        hint: string;
      };
      event: {
        hint: string;
        saved: string;
      };
      itemsCount: string;
      menu: {
        hint: string;
      };
      prices: {
        current: string;
        hint: string;
        noApprovalUi: string;
      };
      realization: {
        current: string;
        hint: string;
        noCustomer: string;
        select: string;
      };
      stepDone: string;
      steps: Record<OfferWizardStepEnum, string>;
      subtitle: string;
      summary: {
        critical: string;
        info: string;
        warning: string;
      };
    };
  };
  details: {
    actions: {
      check: string;
      checkCritical: string;
      checkOk: string;
      checkWarnings: string;
      edit: string;
      hideRealization: string;
      preview: string;
      send: string;
      showRealization: string;
    };
    button: {
      copyAsVersion: string;
      generateOfferFile: string;
      goBackToProcess: string;
      sendOffer: string;
    };
    calendarVisibility: {
      audience: {
        EVERYONE: string;
        NOBODY: string;
        PROCESS_OWNER: string;
        VIEW_ALL_PERMISSION: string;
      };
      reason: {
        MISSING_CUSTOMER: string;
        MISSING_EVENT_DATES: string;
        NOT_PROCESS_OWNER: string;
        STATUS: string;
        STATUS_CANCELED: string;
        TEMPLATE: string;
      };
      reasonLabel: string;
      viewer: {
        hidden: string;
        visible: string;
      };
    };
    createdAt: string;
    customerName: string;
    dishesMargin: string;
    dishesMarginHint: string;
    eventTime: string;
    eventType: string;
    foodCost: string;
    hideRealizationDetails: string;
    location: string;
    margin: string;
    menu: {
      editOffer: string;
    };
    menuStatus: ProcessOfferStatusOptionsTranslation & {
      button: {
        changeStatus: string;
      };
    };
    minimumPrice: string;
    multiOfferName: string;
    noOfGuests: string;
    offerName: string;
    offerPrint: string;
    overriddenPrice: string;
    packagePrice: string;
    predefinedOffer: string;
    realizationContact: string;
    referenceCost: string;
    status: string;
    statusInfo: {
      calendar: {
        label: string;
        notForYou: string;
        ownerOnly: string;
      };
      current: string;
      description: ProcessOfferStatusOptionsTranslation;
      title: string;
    };
    totalGrossPrice: string;
    totalNetPrice: string;
    versions: {
      current: string;
      label: string;
      open: string;
      sent: string;
      tooltip: string;
    };
  };
  dialog: {
    edit: {
      button: {
        cancel: string;
        save: string;
      };
      form: Record<keyof ProcessOfferDialogModel, string> & {
        eventTypePills: {
          empty: string;
          hint: string;
          label: string;
        };
        offerTypeOptions: Record<OfferTypeDto, string>;
        realizationContactButton: string;
        statusOptions: ProcessOfferStatusOptionsTranslation;
      };
      title: string;
    };
    groupCascade: {
      badge: {
        inOfferWindow: string;
        optional: string;
        ownCount: string;
        ownDates: string;
        sameAsOffer: string;
      };
      button: {
        back: string;
        saveOfferOnly: string;
        saveWithGroups: string;
      };
      clampedWarning: string;
      datesChange: string;
      groupDates: string;
      groupGuests: string;
      groupNoDates: string;
      groupNoGuests: string;
      groupPrice: string;
      guestsChange: string;
      minGuestsWarning: string;
      preset: {
        all: string;
        matching: string;
        none: string;
      };
      redistribute: string;
      redistributeHint: string;
      reprice: string;
      repriceHint: string;
      subtitle: string;
      title: string;
      willApplyDates: string;
      willApplyGuests: string;
    };
    selectRealizationContact: {
      button: {
        cancel: string;
        clear: string;
        select: string;
      };
      form: Record<keyof ProcessOfferSelectRealizationContactDialogModel, string>;
      title: string;
    };
  };
  elements: ProcessOfferElementsTranslation;
  emails: ProcessOfferEmailsTranslation;
  form: {
    button: {
      cancel: string;
      submit: string;
    };
    field: Record<keyof ProcessOfferCreateModel, string> & { eventDate: string };
    source: {
      clearSuggestion: string;
      suggestedByEventType: string;
    };
    title: string;
  };
  group: {
    detailsHeader: {
      eventTime: string;
      noOfGuests: string;
      overriddenPrice: string;
      title: string;
    };
  };
  groups: {
    addGroupByClicking: string;
    button: {
      addGroup: string;
      aiChat: string;
      calendar: string;
      collapseAll: string;
      expandAll: string;
      goBackToOfferGroups: string;
      goBackToProcess: string;
      goTo: string;
      insertSet: string;
      planner: string;
      preview: string;
      reorder: string;
      sendOffer: string;
      validationFail: string;
      validationOk: string;
      validationWarnings: string;
    };
    dialog: {
      group: {
        button: {
          cancel: string;
          submit: string;
        };
        form: Record<keyof Omit<ProcessOfferGroupModalModel, 'subgroups'>, string>;
        inlineSubgroups: {
          addButton: string;
          empty: string;
          removeButton: string;
          rowTitle: string;
          title: string;
        };
        titleAdd: string;
        titleEdit: string;
      };
      subgroup: {
        button: {
          cancel: string;
          submit: string;
        };
        form: Record<keyof ProcessOfferSubgroupModalModel, string> & {
          dishCategoryOptions: DishCategoryOptionsTranslation;
          limitsHint: string;
          limitsLocked: string;
          maxDistinct: string;
          perPersonSection: string;
          rangeMax: string;
          rangeMin: string;
          rulesSection: string;
          unitMismatchHint: string;
          unlimitedHint: string;
          varietySection: string;
        };
        titleAdd: string;
        titleEdit: string;
      };
    };
    goTo: {
      close: string;
      title: string;
    };
    insertSet: {
      criteria: {
        clearAll: string;
        counter: string;
        duration: string;
        eventType: string;
        guests: string;
        off: string;
        on: string;
        restoreAll: string;
        tags: string;
      };
      empty: string;
      insert: string;
      mismatched: string;
      noneMatches: string;
      noResults: string;
      outOfTerm: string;
      outOfTermTooltip: string;
      pick: string;
      position: string;
      positionAfter: string;
      positionEnd: string;
      positionStart: string;
      previewEmpty: string;
      pricePerPerson: string;
      searchPlaceholder: string;
      selectHint: string;
      structure: string;
      title: string;
      until: string;
    };
    item: {
      addSubgroupByClicking: string;
      button: {
        addElement: string;
        addSubgroup: string;
        editGroup: string;
      };
      eventTime: string;
      menu: OfferElementTypeOptions & {
        deleteGroup: string;
      };
      minGuests: string;
      minGuestsValue: string;
      modal: {
        delete: DeleteModalTranslation;
      };
      noOfGuests: string;
      optional: {
        exclude: string;
        include: string;
      };
      perPersonValue: string;
      setBadge: string;
      setBadgeNamed: string;
      setCostOverPackage: string;
      setCostPerPerson: string;
      setPricePerPerson: string;
      stat: {
        guests: string;
        minGuests: string;
        minGuestsValue: string;
        setCost: string;
        setPrice: string;
        time: string;
        total: string;
      };
      totalPrice: string;
    };
    margin: string;
    offerStatus: ProcessOfferStatusOptionsTranslation;
    offerTotalPrice: string;
    packagePrice: string;
    packageValidation: {
      allComplete: string;
      missing: string;
      noPackageSubgroups: string;
      summary: string;
      title: string;
      warnings: {
        minGuests: string;
        setUnderpriced: string;
        title: string;
      };
      warningsSummary: string;
    };
    planner: {
      addGroup: string;
      addSubgroup: string;
      delete: string;
      groupNamePlaceholder: string;
      guests: string;
      newGroup: string;
      newSubgroup: string;
      subgroupNamePlaceholder: string;
      subtitle: string;
      title: string;
    };
    referenceCost: string;
    reorder: {
      cancel: string;
      save: string;
      title: string;
    };
    sendOfferWizard: {
      button: {
        back: string;
        cancel: string;
        keepDraft: string;
        next: string;
        send: string;
      };
      checklistBanner: {
        open: string;
        text: string;
      };
      draft: {
        addCafeRoyalInfo: string;
        body: string;
        contentType: string;
        edit: string;
        finishEditing: string;
        generateAi: string;
        hint: string;
        instruction: string;
        instructionPlaceholder: string;
        recipients: string;
        recipientsHint: string;
        subject: string;
      };
      message: {
        empty: string;
        hint: string;
        noSubject: string;
      };
      mode: {
        newThread: string;
        newThreadInfo: string;
        recipients: string;
        recipientsHint: string;
        reply: string;
        replyInfo: string;
        subject: string;
      };
      send: {
        attachment: string;
        keepDraft: string;
        question: string;
        recipient: string;
        sendNow: string;
        sendWarning: string;
        subject: string;
      };
      steps: {
        draft: string;
        message: string;
        mode: string;
        send: string;
      };
      subtitle: string;
      title: string;
      validation: {
        bodyRequired: string;
        messageRequired: string;
        recipientRequired: string;
        subjectRequired: string;
      };
    };
    subgroups: {
      button: {
        manage: string;
      };
      dish: {
        delete: string;
        hideDetails: string;
        noDetails: string;
        showDetails: string;
      };
      dishCategoryOptions: DishCategoryOptionsTranslation;
      distribute: {
        definition: string;
        definitionNoGuests: string;
        definitionOutdated: string;
        label: string;
        offer: string;
        offerBelow: string;
        offerOver: string;
        offerSet: string;
        setBelowBudget: string;
        setBelowMinimum: string;
        setMinimumsOverBudget: string;
        setMoreIssues: string;
        setOverBudget: string;
        setStale: string;
        setUnselectedWithPortions: string;
        stationBelowBudget: string;
        stationOverBudget: string;
        stationStale: string;
        unitMismatch: string;
      };
      editConfig: string;
      mangeSubgroupByClicking: string;
      maxDistinct: string;
      maxDistinctDishes: string;
      maxPortions: string;
      menu: {
        deleteSubgroup: string;
        editSubgroup: string;
      };
      modal: {
        delete: DeleteModalTranslation;
      };
      rules: {
        below: string;
        distinct: string;
        help: string;
        ok: string;
        over: string;
        stale: string;
        unitMismatch: string;
      };
      selectedCount: string;
      selectedOfMax: string;
      set: {
        minPerItem: string;
        portionsHeader: string;
        portionsValue: string;
      };
      setBadge: string;
      table: {
        summaryRow: string;
      };
    };
  };
  pdfPreview: {
    genericError: string;
    legendTitle: string;
    offer: string;
    offerDesc: string;
    offerSelected: string;
    offerSelectedDesc: string;
    offerSelection: string;
    offerSelectionDesc: string;
    realization: string;
    realizationDesc: string;
    scope: {
      all: string;
      allDesc: string;
      current: string;
      currentDesc: string;
    };
    scopeLabel: string;
    template: {
      label: string;
      levelGroup: string;
      levelOffer: string;
      missingDescription: string;
      missingTitle: string;
      noTemplates: string;
      save: string;
      saveAndGenerate: string;
    };
    title: string;
    typeLabel: string;
  };
  processMessagesPreview: {
    button: {
      close: string;
      open: string;
    };
    title: string;
  };
  schedule: ProcessOfferScheduleTranslation;
  sections: ProcessOfferSectionsTranslation;
  sentLock: {
    aiChat: string;
    checklist: string;
    comment: {
      button: string;
      cancel: string;
      hint: string;
      save: string;
      title: string;
    };
    editDialog: string;
    goBack: string;
    howToChange: string;
    newVersion: string;
    newVersionTooltip: string;
    notLatest: string;
    openLatest: string;
    record: string;
    sentAt: string;
    sentTo: string;
    stillEditable: string;
    title: string;
  };
  subgroup: {
    addDishHint: string;
    button: {
      addDishes: string;
      cancel: string;
      finishManagingDishes: string;
      goBackToOffer: string;
      manageDishes: string;
      save: string;
      totalPrice: string;
      unlimitedTotal: string;
    };
    content: {
      department: string;
      foodCost: string;
      minimumSalePrice: string;
      orderMinimumPortions: string;
      price: string;
      priceUnit: string;
      quantity: string;
      quantityUnit: string;
      seasonality: string;
      suggestedSalePrice: string;
      totalPrice: string;
      type: string;
      unit: string;
    };
    dialog: {
      dishes: ProcessOfferDishesTranslation;
    };
    dish: {
      delete: string;
      departmentTitle: string;
      hideDetails: string;
      minPrice: string;
      minQuantity: string;
      priceTitle: string;
      quantityTitle: string;
      showDetails: string;
      totalPriceTitle: string;
      typeTitle: string;
    };
    selection: {
      belowMin: string;
      cost: string;
      costVsPackage: string;
      counter: string;
      distribute: string;
      distributeTooltip: string;
      distributeTooltipStation: string;
      empty: string;
      gramsOfMax: string;
      gramsRequired: string;
      minPortionsInfo: string;
      overDistinctWarning: string;
      perPortion: string;
      portionsCount: string;
      portionsOfMax: string;
      portionsRequired: string;
      quantityTitle: string;
      unlimited: string;
      unselectedWithPortions: string;
    };
  };
};

export const processOfferTranslation: ProcessOfferTranslation = {
  aiChat: {
    authorAssistant: 'Asystent',
    authorUser: 'Ty',
    changeLog: {
      entryNotUndoable: 'Powstało poza ofertą — cofnięcie tego nie zabierze',
      outsideOfferHint:
        'Cofnięcie przywróci samą ofertę. Zadania, notatki i szkice maili zostaną na miejscu.',
      showAll: 'Pokaż wszystkie ({{count}})',
      showLess: 'Zwiń',
      title: 'Zmiany w ofercie',
      titleUndone: 'Zmiany cofnięte',
      undo: 'Cofnij tę turę',
    },
    close: 'Zamknij (Esc)',
    composer: {
      hint: 'Enter wysyła · Shift+Enter nowa linia',
      morePrompts: 'Więcej',
      morePromptsHint: 'Wszystkie podpowiedzi — te same, co na początku rozmowy',
      morePromptsTitle: 'Podpowiedzi',
      placeholder: 'Napisz, co zrobić w ofercie…',
      send: 'Wyślij',
    },
    copy: 'Kopiuj treść',
    copySuccess: 'Skopiowano',
    empty: {
      description:
        'Znam tę ofertę i katalog. Dodam dania i składowe, zmienię liczbę gości albo termin, ' +
        'rozdzielę porcje i przygotuję maila do klienta. Zmiany cofniesz przyciskiem pod odpowiedzią.',
      suggestionsTitle: 'Na dobry początek',
      title: 'W czym pomóc przy tej ofercie?',
    },
    proposal: {
      apply: 'Dodaj zaznaczone ({{count}})',
      clear: 'Wyczyść',
      decrease: 'Mniej — {{name}}',
      dismiss: 'Nie dodawaj',
      followsOffer: {
        HOUR: 'wg czasu trwania',
        PEOPLE: 'wg liczby gości',
      },
      increase: 'Więcej — {{name}}',
      individualPrice: 'wycena indywidualna',
      itemsCount: '{{count}} poz.',
      nothingAdded: 'Nic nie zostało dodane.',
      pieces: '{{count}} szt.',
      pieceUnit: 'szt.',
      quantity: 'Ilość — {{name}}',
      selectAll: 'Zaznacz wszystkie',
      setGuestCount: 'Po sztuce na gościa ({{count}})',
      status: {
        APPLIED: 'Dodane do oferty',
        DISMISSED: 'Propozycja odrzucona',
        PENDING: 'Do zaznaczenia',
      },
      title: 'Zaznacz, co dodać',
      total: 'razem od {{price}}\u00a0zł',
      unitPrice: {
        HOUR: 'od {{price}}\u00a0zł/godz.',
        OTHER: 'od {{price}}\u00a0zł',
        PEOPLE: 'od {{price}}\u00a0zł/os.',
        UNIT: 'od {{price}}\u00a0zł/szt.',
      },
    },
    question: {
      dishes: {
        few: '{{count}} dania',
        many: '{{count}} dań',
        one: '{{count}} danie',
      },
      dishSources: {
        add: 'Dodaj dania ({{count}})',
        allDepartments: 'wszystkie działy',
        allDishTypes: 'wszystkie rodzaje',
        answered: 'Dobór dań zatwierdzony',
        backToEventType: 'Wróć do „{{name}}”',
        change: 'Zmień',
        count: 'Liczba dań — {{name}}',
        decrease: 'Mniej dań — {{name}}',
        departments: 'Dział',
        dishTypes: 'Rodzaj',
        done: 'Gotowe',
        increase: 'Więcej dań — {{name}}',
        matching: 'pasuje {{dishes}}',
        matchingFewer: 'pasuje tylko {{dishes}}',
        narrowed: 'Dania pod typ wydarzenia „{{name}}”',
        narrowingWithoutDishes:
          'Typ wydarzenia „{{name}}” nie ma jeszcze dań z tagami — pokazuję cały katalog.',
        noMatching: 'brak pasujących dań — zmień dział albo rodzaj',
        showWholeCatalog: 'Pokaż cały katalog',
        skippedSubgroup: 'tej podgrupy nie wypełnię',
        title: 'Z czego dobrać dania?',
        wholeCatalog: 'Cały katalog dań',
      },
      dishUnit: {
        few: 'dania',
        many: 'dań',
        one: 'danie',
      },
      skip: 'Pomiń',
      skipped: 'Karta pominięta',
      subgroups: {
        answered: 'Do wypełnienia',
        clear: 'Wyczyść',
        empty: 'pusta',
        filled: 'ma {{dishes}}',
        hideFilled: 'Ukryj wypełnione',
        next: 'Dalej ({{count}})',
        selectEmpty: 'Zaznacz puste',
        showFilled: 'Pokaż wypełnione ({{count}})',
        title: 'Które podgrupy wypełnić?',
      },
      userMessage: {
        dishSources: 'Dodaj dania:',
        subgroups: 'Wypełnij podgrupy: {{list}}.',
        wholeCatalog: 'Szukaj w całym katalogu, bez zawężenia do typu „{{name}}”.',
      },
    },
    reportTooltip:
      'Zgłoś tę rozmowę do supportu — dołączymy jej zapis i adres oferty, ty dopisujesz tylko, ' +
      'co poszło źle.',
    subtitleGuests: '{{count}} os.',
    suggestedReplies: 'Podpowiedzi odpowiedzi',
    suggestions: {
      equipment: {
        hint: 'Krzesła, stoły, nakrycia — ilości poprawisz przed dodaniem',
        label: 'Zaproponuj sprzęt dla gości',
        prompt:
          'Zaproponuj sprzęt potrzebny na to wydarzenie (krzesła, stoły, nakrycia), z ilościami ' +
          'dopasowanymi do liczby gości.',
      },
      fillDishes: {
        hint: 'Zaznaczysz podgrupy, a potem działy i rodzaje dań — asystent sam dobierze dania',
        label: 'Dobierz dania',
        prompt:
          'Chcę szybko dobrać dania do podgrup tej oferty. Pokaż kartę z podgrupami do wypełnienia.',
      },
      notesAndEmails: {
        hint: 'Wyciągnie ustalenia z notatek i maili procesu i porówna je z ofertą — niczego nie zmieni',
        label: 'Analizuj notatki i maile',
        prompt:
          'Przeanalizuj notatki i maile z procesu. Wypisz, co ustaliliśmy z klientem (termin, liczba ' +
          'gości, lokalizacja, menu i diety, napoje, obsługa, budżet), porównaj to z ofertą i powiedz, ' +
          'co się różni, czego brakuje i o co dopytać klienta. Niczego nie zmieniaj.',
      },
      offerEmail: {
        hint:
          'Asystent przeczyta korespondencję z procesu, napisze odpowiedź i dołączy PDF z ofertą. ' +
          'Nic nie wyśle — wersja robocza czeka w wątku.',
        label: 'Email z ofertą',
        prompt:
          'Przygotuj wersję roboczą maila do klienta z tą ofertą w załączniku. Najpierw przeczytaj ' +
          'maile procesu i podsumowanie oferty, potem odpowiedz na najnowszą wiadomość, nawiązując ' +
          'do tego, o co klient pytał. Wspomnij termin, liczbę gości i cenę. Nie wysyłaj — zostaw ' +
          'wersję roboczą.',
      },
      review: {
        hint: 'Wskaże luki i możliwe pomyłki — niczego nie zmieni',
        label: 'Sprawdź, czego brakuje',
        prompt:
          'Przejrzyj tę ofertę i wypisz, czego w niej brakuje albo co wygląda na pomyłkę — np. brak ' +
          'napojów, obsługi lub sprzętu, dziwne ilości, podgrupy bez dań. Niczego nie zmieniaj, tylko wskaż.',
      },
      summary: {
        hint: 'Termin, goście, menu i składowe w kilku zdaniach',
        label: 'Podsumuj ofertę',
        prompt:
          'Podsumuj krótko tę ofertę: termin, liczba gości, grupy z najważniejszymi daniami ' +
          'i składowe. Niczego nie zmieniaj.',
      },
    },
    thinking: 'Pracuję nad tym…',
    thinkingLong: 'Wciąż pracuję — większe zmiany potrafią chwilę potrwać',
    title: 'Asystent AI',
    today: 'Dziś',
    yesterday: 'Wczoraj',
  },
  availability: {
    button: {
      addRule: 'Dodaj regułę',
      removeRule: 'Usuń regułę',
    },
    day: {
      [DayOfWeekDto.Friday]: 'Pt',
      [DayOfWeekDto.Monday]: 'Pn',
      [DayOfWeekDto.Saturday]: 'Sb',
      [DayOfWeekDto.Sunday]: 'Nd',
      [DayOfWeekDto.Thursday]: 'Cz',
      [DayOfWeekDto.Tuesday]: 'Wt',
      [DayOfWeekDto.Wednesday]: 'Śr',
    },
    emptyHint: 'Brak reguł oznacza, że oferta jest zawsze dostępna.',
    field: {
      dayOfMonth: 'Dzień miesiąca',
      daysOfWeek: 'Dni tygodnia',
      frequency: 'Częstotliwość',
      intervalValue: 'Co N',
      ruleType: 'Typ reguły',
      specificDate: 'Konkretna data',
      validFrom: 'Obowiązuje od',
      validTo: 'Obowiązuje do',
    },
    frequency: {
      [RecurrenceFrequencyDto.Daily]: 'Codziennie',
      [RecurrenceFrequencyDto.Monthly]: 'Co miesiąc',
      [RecurrenceFrequencyDto.Weekly]: 'Co tydzień',
      [RecurrenceFrequencyDto.Yearly]: 'Co rok',
    },
    ruleType: {
      [AvailabilityRuleTypeDto.Recurring]: 'Cyklicznie',
      [AvailabilityRuleTypeDto.SpecificDate]: 'Konkretna data',
    },
    summary: {
      always: 'Zawsze dostępna',
      draft: 'Nowa reguła — uzupełnij szczegóły',
      intervalEvery: 'Co {{interval}} jednostek',
      monthDay: 'dzień miesiąca: {{day}}',
      on: 'Konkretna data: {{date}}',
      rangeBoth: 'od {{from}} do {{to}}',
      rangeFrom: 'od {{from}}',
      rangeTo: 'do {{to}}',
      weeklyDays: 'dni: {{days}}',
    },
    title: 'Dostępność',
  },
  calendar: {
    title: 'Kalendarz tygodnia',
  },
  checklist: {
    allClear: 'Nic do poprawy — oferta wygląda na kompletną.',
    button: {
      add: 'Dodaj',
      back: 'Wróć i popraw',
      change: 'Zmień',
      close: 'Zamknij',
      continue: 'Kontynuuj mimo to',
      distribute: 'Rozdziel',
      explain: 'Wyjaśnij',
      show: 'Pokaż',
    },
    items: {
      discountAwaitingApproval:
        'Rabat {{price}} zł jest poniżej ceny minimalnej {{minimum}} zł i czeka na akceptację.',
      discountRejected: 'Rabat {{price}} zł został odrzucony, a cena minimalna to {{minimum}} zł.',
      dishesOutOfSeason:
        '„{{group}}" → „{{subgroup}}": poza sezonem w terminie wydarzenia ({{count}}): {{names}}. Potwierdź dostępność z kuchnią.',
      dishesPriceOutOfTerm:
        '„{{group}}" → „{{subgroup}}": bez cennika na termin wydarzenia ({{count}}): {{names}}. Ceny policzone współczynnikiem, nie realnym cennikiem.',
      emptyGroup: 'Grupa „{{group}}" nie ma jeszcze żadnej podgrupy.',
      emptySubgroup: '„{{group}}" → „{{subgroup}}": podgrupa nie ma żadnego dania.',
      groupDatesWithoutOfferDates:
        'Grupa „{{group}}" ma termin {{groupFrom}} – {{groupTo}}, a oferta nie ma ustawionego terminu.',
      groupGuestsDiffer:
        'Grupa „{{group}}" obsługuje {{groupGuests}} os., a oferta mówi o {{offerGuests}}. Osobna liczba dla lunchu jest w porządku — sprawdź tylko, czy tak ma być.',
      groupOutsideOfferDates:
        'Grupa „{{group}}" ma termin {{groupFrom}} – {{groupTo}}, poza terminem oferty {{offerFrom}} – {{offerTo}}. Zmiana terminu oferty schodzi tylko na grupy zaznaczone przy zapisie — sprawdź, czy tak ma być.',
      noCustomer:
        'Proces nie ma przypisanego klienta — bez niego oferta nie przejdzie do ofertowania.',
      noElement: {
        [OfferElementTypeDto.Decoration]: 'Oferta nie zawiera dekoracji.',
        [OfferElementTypeDto.DishPackages]: 'Oferta nie zawiera zestawów dań.',
        [OfferElementTypeDto.DrinkPackages]: 'Oferta nie zawiera zestawów napojów.',
        [OfferElementTypeDto.Equipment]: 'Oferta nie zawiera sprzętu.',
        [OfferElementTypeDto.Service]: 'Oferta nie zawiera obsługi.',
        [OfferElementTypeDto.Transport]: 'Oferta nie zawiera transportu.',
      },
      noGroups: 'Oferta nie ma żadnej grupy z daniami.',
      noGuests: 'Nie podano liczby gości — ceny za osobę i składowe nie policzą się poprawnie.',
      noLocation: 'Nie podano lokalizacji wydarzenia.',
      noRealizationContact:
        'Brak kontaktu realizacyjnego — będzie wymagany przy akceptacji do realizacji.',
      pastEvent: 'Termin wydarzenia ({{date}}) jest w przeszłości.',
      priceBelowMinimum: 'Cena oferty {{price}} zł jest poniżej ceny minimalnej {{minimum}} zł.',
      subgroupRules: '„{{group}}" → „{{subgroup}}": dania nie zgadzają się z regułami podgrupy.',
    },
    nonBlocking: 'Lista niczego nie blokuje — ofertę możesz wysłać także z ostrzeżeniami.',
    skipped: {
      reason: 'Powód:',
      title: 'Świadomie pominięte',
    },
    subgroupIssues: {
      belowBudget: 'poniżej budżetu podgrupy: {{allocation}}',
      belowOrderMinimum: 'poniżej minimum zamówienia ({{count}}): {{names}}',
      distributionOutdated:
        'ilości różnią się od tego, co dałby „Rozdziel" — kliknij go, żeby je wyrównać',
      distributionOutdatedDishes:
        'rozdział porcji jest nieaktualny (dania do poprawy: {{count}}) — kliknij „Rozdziel"',
      incompletePick: 'niepełny wybór dań: {{selected}} z {{required}}',
      minimumsOverBudget:
        'same minima zamówienia zaznaczonych dań przekraczają budżet ({{allocation}}) — „Rozdziel" tego nie naprawi, odznacz któreś z dań',
      overBudget: 'ponad budżet podgrupy: {{allocation}}',
      tooFewDistinct: 'wybrano {{selected}} różnych dań, wymagane min. {{required}}',
      unitMismatch:
        'dania na sztuki w budżecie gramowym ({{count}}): {{names}} — rozdział porcji ich nie rusza, więc idą do klienta obok budżetu',
    },
    summary: 'Błędy: {{critical}} · ostrzeżenia: {{warnings}}',
    title: 'Sprawdź ofertę',
    wizard: {
      button: {
        goToStep: 'Popraw',
        list: 'Lista',
        next: 'Dalej',
        prev: 'Wstecz',
        save: 'Zapisz',
        steps: 'Kreator',
      },
      customer: {
        current: 'Klient procesu: {{name}}',
        goToProcess: 'Przejdź do procesu',
        hint:
          'Klienta ustawia się na procesie, nie na ofercie — oferta zawsze bierze tego, którego ma ' +
          'jej proces. Bez niego oferta nie przejdzie w Ofertowanie.',
      },
      elements: {
        hint:
          'Brak składowej nie musi być błędem. Dodaj ją albo wyjaśnij, dlaczego jej nie ma — ' +
          'ostrzeżenie zniknie, a w ofercie zostanie ślad decyzji.',
      },
      event: {
        hint: 'Liczba gości, termin i lokalizacja. Statusu oferty ten formularz nie zmienia.',
        saved: 'Zapisano.',
      },
      itemsCount: 'do sprawdzenia: {{count}}',
      menu: {
        hint:
          '„Rozdziel" wyrównuje porcje tak samo jak przycisk na karcie podgrupy. Sam wybór dań ' +
          '(sezon, cennik, komplet) poprawia się w ofercie — „Pokaż" zamyka kreator i prowadzi ' +
          'na miejsce.',
      },
      prices: {
        current: 'Cena oferty: {{price}} zł · cena minimalna: {{minimum}} zł',
        hint: 'Cena to suma dań i składowych — podnosi się ją w podgrupach, nie tutaj.',
        noApprovalUi:
          'Akceptacji rabatu nie da się dziś obsłużyć w aplikacji — ustalcie ją poza systemem.',
      },
      realization: {
        current: 'Kontakt realizacji: {{contact}}',
        hint: 'Do wysyłki oferty niepotrzebny — będzie wymagany przy przekazaniu do realizacji.',
        noCustomer: 'Najpierw wybierz klienta procesu: kontaktów szukamy wśród jego osób.',
        select: 'Wybierz kontakt realizacji',
      },
      stepDone: 'Wszystko gotowe — możesz iść dalej.',
      steps: {
        [OfferWizardStepEnum.CUSTOMER]: 'Klient',
        [OfferWizardStepEnum.ELEMENTS]: 'Składowe',
        [OfferWizardStepEnum.EVENT]: 'Dane wydarzenia',
        [OfferWizardStepEnum.MENU]: 'Menu i zestawy',
        [OfferWizardStepEnum.PRICES]: 'Ceny',
        [OfferWizardStepEnum.REALIZATION]: 'Realizacja',
        [OfferWizardStepEnum.SUMMARY]: 'Podsumowanie',
      },
      subtitle:
        'Przejdź kroki po kolei albo skocz do tego, który chcesz poprawić. Nic tu nie blokuje ' +
        'wysyłki.',
      summary: {
        critical: 'Błędy ({{count}})',
        info: 'Do uzupełnienia później ({{count}})',
        warning: 'Ostrzeżenia ({{count}})',
      },
    },
  },
  details: {
    actions: {
      check: 'Sprawdź ofertę',
      checkCritical: 'Do poprawy: {{count}}',
      checkOk: 'Oferta sprawdzona',
      checkWarnings: 'Uwagi: {{count}}',
      edit: 'Edytuj dane',
      hideRealization: 'Ukryj szczegóły realizacji',
      preview: 'Podgląd PDF',
      send: 'Wyślij ofertę',
      showRealization: 'Pokaż szczegóły realizacji',
    },
    button: {
      copyAsVersion: 'Nowa wersja',
      generateOfferFile: 'Pobierz realizację',
      goBackToProcess: 'Proces',
      sendOffer: 'Wyślij ofertę',
    },
    calendarVisibility: {
      audience: {
        EVERYONE: 'Widoczna w kalendarzu dla wszystkich',
        NOBODY: 'Niewidoczna w kalendarzu',
        PROCESS_OWNER: 'Tylko opiekun procesu',
        VIEW_ALL_PERMISSION: 'Tylko sprzedaż i zarząd',
      },
      reason: {
        MISSING_CUSTOMER: 'brak klienta w procesie',
        MISSING_EVENT_DATES: 'brak terminu wydarzenia',
        NOT_PROCESS_OWNER: 'nie jesteś opiekunem procesu',
        STATUS: 'ten status nie wchodzi na Twój kalendarz',
        STATUS_CANCELED: 'anulowana',
        TEMPLATE: 'szablon oferty',
      },
      reasonLabel: 'Powód',
      viewer: {
        hidden: 'Ty jej nie widzisz',
        visible: 'Ty ją widzisz',
      },
    },
    createdAt: 'Data utworzenia oferty',
    customerName: 'Klient',
    dishesMargin: 'Marża na daniach',
    dishesMarginHint:
      'Liczona wyłącznie na daniach: przychód z jedzenia minus food cost porcji. Składowe (transport, obsługa, sprzęt) mają w katalogu tylko cenę sprzedaży, bez kosztu, więc nie wchodzą do tego wyliczenia.',
    eventTime,
    eventType: 'Typ wydarzenia',
    foodCost: 'Food cost dań',
    hideRealizationDetails: 'Ukryj szczegóły realizacji',
    location: 'Lokalizacja',
    margin: 'Marża',
    menu: {
      editOffer: 'Edytuj ofertę',
    },
    menuStatus: {
      ...processOfferStatusOptionsTranslation,
      button: {
        changeStatus: 'Zmień status',
      },
    },
    minimumPrice: 'Cena minimalna',
    multiOfferName: 'Nazwa grupy ofert',
    noOfGuests,
    offerName: 'Nazwa oferty',
    offerPrint: 'Szablon oferty',
    overriddenPrice,
    packagePrice: 'Cena pakietowa',
    predefinedOffer: 'Oferta predefiniowana',
    realizationContact: 'Kontakt na realizacji',
    referenceCost: 'Koszt (ref.)',
    status: 'Status oferty',
    statusInfo: {
      calendar: {
        label: 'Kalendarz',
        notForYou: 'Ty tego statusu nie widzisz',
        ownerOnly: 'tylko Twoje procesy',
      },
      current: 'aktualny status',
      description: {
        CANCELED: 'Oferta została anulowana — nie będzie realizowana.',
        CONFIRMED_FOR_REALIZATION: 'Sprzedaż potwierdzona i przekazana do realizacji.',
        DRAFT: 'Oferta w przygotowaniu — jeszcze nieprzedstawiona klientowi.',
        EXECUTED: 'Usługa (wydarzenie) została wykonana.',
        FINISHED: 'Zlecenie zakończone i rozliczone.',
        IN_REALIZATION: 'Zlecenie jest aktualnie w trakcie realizacji.',
        OFFER_ACCEPTED: 'Klient zaakceptował ofertę — sprzedana.',
        OFFERING: 'Oferta przedstawiona klientowi, czeka na jego decyzję.',
        PAYED: 'Płatność za zlecenie została otrzymana.',
      },
      title: 'Statusy oferty — co oznaczają',
    },
    totalGrossPrice: 'Cena brutto',
    totalNetPrice: 'Cena netto',
    versions: {
      current: 'otwarta teraz',
      label: 'Wersje',
      open: 'kliknij, żeby otworzyć',
      sent: 'wysłana {{date}}',
      tooltip: 'Wersja {{version}} · {{status}}',
    },
  },
  dialog: {
    edit: {
      button: {
        cancel: 'Anuluj',
        save: 'Zapisz',
      },
      form: {
        availabilityRules: 'Reguły dostępności',
        eventDateFrom: 'Data rozpoczęcia',
        eventDateTo: 'Data zakończenia',
        eventTypeId: 'Typ wydarzenia',
        eventTypePills: {
          empty: 'Nie ma jeszcze żadnego typu wydarzenia — dodasz go w Słownikach.',
          hint: 'Wybierz, aby ograniczyć dania, z których będziesz budować ofertę.',
          label: 'Typ wydarzenia',
        },
        location: 'Lokalizacja',
        minimumPrice: 'Cena minimalna',
        name: 'Nazwa',
        noOfGuests,
        offerPrintId: 'Szablon wydruku (nadpisanie)',
        offerType: 'Typ oferty',
        offerTypeOptions: {
          PACKAGE: 'Pakietowa',
          REGULAR: 'Zwykła',
        },
        packagePricePerPerson: 'Cena za osobę (PLN)',
        predefinedOfferId: 'Oferta predefiniowana',
        price: 'Cena wyliczona',
        realizationContact: 'Kontakt realizacji',
        realizationContactButton: 'Wybierz kontakt realizacji',
        status: 'Status',
        statusOptions: processOfferStatusOptionsTranslation,
      },
      title: 'Edycja oferty',
    },
    groupCascade: {
      badge: {
        inOfferWindow: 'w oknie oferty',
        optional: 'opcjonalna',
        ownCount: 'własna liczba',
        ownDates: 'termin spoza oferty',
        sameAsOffer: 'jak oferta',
      },
      button: {
        back: 'Wstecz',
        saveOfferOnly: 'Zapisz tylko ofertę',
        saveWithGroups: 'Zapisz i zaktualizuj grupy ({{count}})',
      },
      clampedWarning: 'termin wyszedłby poza ofertę — zostanie dociśnięty do jej okna',
      datesChange: 'Początek oferty: {{from}} → {{to}}',
      groupDates: 'termin: {{from}} – {{to}}',
      groupGuests: '{{guests}} os.',
      groupNoDates: 'bez terminu',
      groupNoGuests: 'bez własnej liczby',
      groupPrice: 'zestaw: {{before}} zł → {{after}} zł',
      guestsChange: 'Liczba osób w ofercie: {{from}} → {{to}}',
      minGuestsWarning: 'poniżej minimum grupy ({{min}} os.)',
      preset: {
        all: 'Wszystkie grupy',
        matching: 'Grupy zgodne z ofertą ({{count}})',
        none: 'Żadna',
      },
      redistribute: 'Rozdziel porcje od nowa w zaznaczonych grupach',
      redistributeHint:
        'W zestawie zmienią się tylko ilości zaznaczonych dań. Na stacji podział decyduje też o tym, które dania się mieszczą. Podgrupy bez reguły na osobę zostaną nietknięte.',
      reprice: 'Przelicz składowe liczone na osobę i za godzinę (także ceny progowe)',
      repriceHint:
        'Obsługa, transport i sprzęt dostaną ilość z nowej liczby osób albo z nowej długości grupy. Składowe z cennikiem progowym wejdą w próg właściwy dla nowej wartości, więc ich cena jednostkowa może się zmienić. Pozycje z ceną wpisaną ręcznie zostaną nietknięte.',
      subtitle:
        'Każda grupa trzyma własną liczbę osób i własny termin — to one napędzają cenę zestawu, budżet porcji, wycenę obsługi i transportu oraz wydruki dla kuchni.',
      title: 'Zaktualizować grupy oferty?',
      willApplyDates: '→ po zapisie: {{from}} – {{to}}',
      willApplyGuests: '→ po zapisie: {{guests}} os.',
    },
    selectRealizationContact: {
      button: {
        cancel: 'Anuluj',
        clear: 'Wyczyść',
        select: 'Wybierz',
      },
      form: {
        contact: 'Kontakt realizacja',
        phone: 'Telefon',
        selectedContact: 'Wybierz osobę do kontaktu z listy',
      },
      title: 'Wybierz kontakt realizacji',
    },
  },
  elements: processOfferElementsTranslation,
  emails: processOfferEmailsTranslation,
  form: {
    button: {
      cancel: 'Anuluj',
      submit: 'Dalej',
    },
    field: {
      availabilityRules: 'Reguły dostępności',
      eventDate: 'Termin wydarzenia',
      eventDateFrom: 'Data rozpoczęcia',
      eventDateTo: 'Data zakończenia',
      eventTypeId: 'Typ wydarzenia',
      location: 'Lokalizacja',
      name: 'Nazwa',
      noOfGuests,
      predefinedOfferId: 'Oferta predefiniowana',
    },
    source: {
      clearSuggestion: 'Wyczyść',
      suggestedByEventType: 'Podpowiedziane przez typ wydarzenia „{{eventType}}"',
    },
    title: 'Podaj parametry tworzonej oferty',
  },
  group: {
    detailsHeader: {
      eventTime,
      noOfGuests,
      overriddenPrice,
      title: 'Szczegóły grupy',
    },
  },
  groups: {
    addGroupByClicking: 'Dodaj pierwszą grupę',
    button: {
      addGroup: 'Dodaj grupę',
      aiChat: 'Asystent AI',
      calendar: 'Kalendarz tygodnia terminu',
      collapseAll: 'Zwiń wszystko',
      expandAll: 'Rozwiń wszystko',
      goBackToOfferGroups: 'Grupy ofert',
      goBackToProcess: 'Proces',
      goTo: 'Idź do',
      insertSet: 'Wstaw zestaw grup',
      planner: 'Plan grup',
      preview: 'Podgląd PDF',
      reorder: 'Kolejność',
      sendOffer: 'Wyślij ofertę',
      validationFail: 'Oferta ma błędy do poprawy — kliknij, by je zobaczyć',
      validationOk: 'Oferta sprawdzona — nic do poprawy',
      validationWarnings: 'Bez błędów, ale {{count}} ostrzeżeń — kliknij po szczegóły',
    },
    dialog: {
      group: {
        button: {
          cancel: 'Anuluj',
          submit: 'Zapisz',
        },
        form: {
          comment: 'Komentarz - pojawi się na wydruku dla działu realizacji',
          eventFrom: 'Data rozpoczęcia',
          eventTo: 'Data zakończenia',
          minGuests: 'Min. liczba osób',
          name: 'Nazwa grupy',
          noOfGuests,
          optional: 'Grupa opcjonalna (klient dolicza)',
          surchargePerPerson: 'Dopłata / os. (zł)',
        },
        inlineSubgroups: {
          addButton: 'Dodaj kolejną podgrupę',
          empty: 'Brak podgrup. Możesz dodać je teraz lub później.',
          removeButton: 'Usuń podgrupę',
          rowTitle: 'Podgrupa',
          title: 'Podgrupy (opcjonalnie)',
        },
        titleAdd: 'Podaj szczegóły grupy',
        titleEdit: 'Zaktualizują grupę',
      },
      subgroup: {
        button: {
          cancel: 'Anuluj',
          submit: 'Zapisz',
        },
        form: {
          comment: 'Komentarz - pojawi się na wydruku dla działu realizacji',
          dishCategory: 'Kategoria',
          dishCategoryOptions: dishCategoryOptionsTranslation,
          limitsHint:
            'Oba wymiary są niezależne i opcjonalne — np. „3 różne dania po 4 porcje na osobę". ' +
            'Przekroczenie limitu nie blokuje zapisu, pokazuje się jako ostrzeżenie na liście kontrolnej.',
          limitsLocked:
            'Reguły przyszły z pakietu i zostały ustalone przy szablonie — tutaj klient wybiera ' +
            'z gotowej puli, więc pola są tylko do odczytu.',
          maxDistinct: 'Maks. dań',
          maxDistinctDishes: 'Maks. różnych dań',
          maxPerPerson: 'Maks. / os.',
          minDistinctDishes: 'Min. dań',
          minPerPerson: 'Min. / os.',
          name: 'Nazwa podgrupy',
          perPersonSection: 'Ilość na osobę',
          perPersonUnit: 'Jednostka',
          rangeMax: 'maks',
          rangeMin: 'min',
          rulesSection: 'Reguły podgrupy (opcjonalne)',
          unitMismatchHint:
            'Przy gramaturze dania na sztuki wypadają z rozdziału porcji i z wyceny wagowej. ' +
            'Ta podgrupa ma ich {{count}}: {{names}}. Zapis niczego nie blokuje — ustaw im ' +
            'gramaturę w katalogu albo zostaw porcje.',
          unlimited: 'Bez limitu (w cenie zestawu)',
          unlimitedHint:
            'Wszystko z tej podgrupy jest podawane bez ograniczeń przez cały czas grupy — np. ' +
            'przerwa kawowa albo open bar. Liczy się tylko cena zestawu za osobę: dania nie mają ' +
            'porcji ani wartości. Limit różnych dań dalej działa, np. „8 drinków do wyboru”.',
          varietySection: 'Różnych dań',
        },
        titleAdd: 'Podaj szczegóły podgrupy',
        titleEdit: 'Zaktualizuj podgrupę',
      },
    },
    goTo: {
      close: 'Zamknij',
      title: 'Idź do grupy lub podgrupy',
    },
    insertSet: {
      criteria: {
        clearAll: 'Wyczyść zawężenia',
        counter: 'Pasuje {{matched}} z {{total}}',
        duration: '{{hours}} h',
        eventType: 'Typ wydarzenia',
        guests: '{{count}} os.',
        off: 'Bez zawężeń — kliknij pastylkę, żeby wrócić do dopasowania:',
        on: 'Zawężone do tej oferty:',
        restoreAll: 'Przywróć dopasowanie',
        tags: 'Tagi ({{count}})',
      },
      empty: 'Brak zestawów dostępnych na datę tej oferty. Dodaj je w „Zestawy grup".',
      insert: 'Wstaw',
      mismatched: 'Niedopasowane ({{count}})',
      noneMatches:
        'Żaden zestaw nie mieści się w tych warunkach — zdejmij pastylkę albo zajrzyj niżej.',
      noResults: 'Brak wyników',
      outOfTerm: '{{count}} dań poza terminem',
      outOfTermTooltip:
        'Część dań tego zestawu ma sezonowość lub cennik niepokrywające daty wydarzenia. Zestaw da się wstawić — dania będą oznaczone ikoną.',
      pick: 'Wybierz ten zestaw',
      position: 'Miejsce',
      positionAfter: 'Po: {{name}}',
      positionEnd: 'Na końcu',
      positionStart: 'Na początku',
      previewEmpty: 'Ten zestaw nie ma jeszcze grup',
      pricePerPerson: '{{price}} zł/os',
      searchPlaceholder: 'Szukaj zestawu…',
      selectHint: 'Wybierz zestaw z listy, aby zobaczyć szczegóły i wstawić',
      structure: '{{groups}} gr. · {{subgroups}} podgr.',
      title: 'Wstaw zestaw grup',
      until: 'do',
    },
    item: {
      addSubgroupByClicking: 'Dodaj pierwszą podgrupę',
      button: {
        addElement: 'Dodaj do grupy transport, obsługę, sprzęt lub dekoracje',
        addSubgroup: 'Dodaj podgrupę',
        editGroup: 'Edytuj grupę',
      },
      eventTime,
      menu: {
        ...offerElementTypeOptions,
        deleteGroup: 'Usuń grupę',
      },
      minGuests: 'Min. liczba osób',
      minGuestsValue: 'min. {{count}} os.',
      modal: {
        delete: deleteModalTranslation,
      },
      noOfGuests,
      optional: {
        exclude: 'Usuń grupę z oferty',
        include: 'Dołącz grupę do oferty',
      },
      perPersonValue: '{{price}}/os',
      setBadge: 'Zestaw',
      setBadgeNamed: 'Zestaw: {{name}}',
      setCostOverPackage: 'Koszt wybranych porcji przekracza cenę pakietu na osobę',
      setCostPerPerson: 'Koszt wybranych porcji na osobę',
      setPricePerPerson: 'Cena zestawu za osobę',
      stat: {
        guests: 'Goście',
        minGuests: 'Minimum',
        minGuestsValue: '{{count}} os.',
        setCost: 'Koszt dań',
        setPrice: 'Cena zestawu',
        time: 'Termin',
        total: 'Suma grupy',
      },
      totalPrice: 'Suma grupy',
    },
    margin: 'Marża',
    offerStatus: processOfferStatusOptionsTranslation,
    offerTotalPrice: 'Suma oferty',
    packagePrice: 'Cena pakietowa',
    packageValidation: {
      allComplete: 'Wszystkie podgrupy mają komplet wybranych dań.',
      missing: 'brakuje {{count}}',
      noPackageSubgroups: 'Ta oferta nie ma podgrup z limitem wyboru.',
      summary: 'Niekompletny wybór w {{count}} podgrupach — uzupełnij poniżej.',
      title: 'Walidacja wyboru dań',
      warnings: {
        minGuests: 'Grupa „{{group}}" wymaga min. {{min}} os. — oferta ma {{guests}}',
        setUnderpriced:
          'Zestaw „{{name}}": sprzedany za {{declared}} zł/os, a z dań wychodzi {{computed}} zł/os',
        title: 'Ostrzeżenia',
      },
      warningsSummary: 'Wybór kompletny, ale {{count}} ostrzeżeń — sprawdź poniżej.',
    },
    planner: {
      addGroup: 'Dodaj grupę',
      addSubgroup: 'Podgrupa',
      delete: 'Usuń',
      groupNamePlaceholder: 'Nazwa grupy…',
      guests: 'Liczba gości',
      newGroup: 'Nowa grupa',
      newSubgroup: 'Nowa podgrupa',
      subgroupNamePlaceholder: 'Nazwa podgrupy…',
      subtitle: 'klikaj i pisz — bez trybów edycji',
      title: 'Plan grup',
    },
    referenceCost: 'Koszt (ref.)',
    reorder: {
      cancel: 'Anuluj',
      save: 'Zapisz',
      title: 'Zmień kolejność grup i podgrup',
    },
    sendOfferWizard: {
      button: {
        back: 'Wstecz',
        cancel: 'Anuluj',
        keepDraft: 'Zapisz szkic',
        next: 'Dalej',
        send: 'Wyślij',
      },
      checklistBanner: {
        open: 'Otwórz kreator',
        text:
          'Sprawdź ofertę — błędy: {{critical}}, ostrzeżenia: {{warnings}}. Wysyłki nic nie ' +
          'blokuje, ale warto zerknąć przed mailem do klienta.',
      },
      draft: {
        addCafeRoyalInfo: 'Dopisz informacje o Cafe Royal',
        body: 'Treść wiadomości',
        contentType: 'Rodzaj treści',
        edit: 'Edytuj treść',
        finishEditing: 'Zakończ edycję',
        generateAi: 'Wygeneruj odpowiedź AI',
        hint: 'Oferta jest już dołączona do szkicu. Treść możesz wygenerować przez AI albo napisać samodzielnie.',
        instruction: 'Wskazówka dla AI',
        instructionPlaceholder: 'Napisz, co ma znaleźć się w wiadomości…',
        recipients: 'Do',
        recipientsHint: 'Kilka adresów oddziel przecinkiem.',
        subject: 'Temat maila',
      },
      message: {
        empty: 'W tym procesie nie ma jeszcze wysłanych maili — wróć i utwórz nowy wątek.',
        hint: 'Wybierz wiadomość, na którą odpowiadamy ofertą.',
        noSubject: '(bez tematu)',
      },
      mode: {
        newThread: 'Utwórz nowy wątek mailowy z dołączoną ofertą',
        newThreadInfo:
          'Założymy nowy wątek w procesie, dołączymy do niego ofertę i pokażemy szkic do akceptacji.',
        recipients: 'Do',
        recipientsHint: 'Kilka adresów oddziel przecinkiem.',
        reply: 'Odpowiedz na jedną z wiadomości procesu',
        replyInfo: 'Na następnym kroku wybierzesz wiadomość, na którą odpowiadamy.',
        subject: 'Temat maila',
      },
      send: {
        attachment: 'Załącznik',
        keepDraft: 'Zostaw jako szkic — wyślę później',
        question: 'Wysyłamy teraz czy zostawiamy szkic?',
        recipient: 'Do',
        sendNow: 'Wyślij teraz',
        sendWarning: 'Wiadomość wyjdzie do klienta natychmiast po zatwierdzeniu.',
        subject: 'Temat',
      },
      steps: {
        draft: 'Szkic',
        message: 'Wiadomość',
        mode: 'Sposób',
        send: 'Wysyłka',
      },
      subtitle: 'Szkic z ofertą powstaje tutaj — bez wychodzenia do maili procesu.',
      title: 'Wyślij ofertę',
      validation: {
        bodyRequired: 'Wiadomość nie ma treści.',
        messageRequired: 'Wybierz wiadomość, na którą odpowiadamy.',
        recipientRequired: 'Podaj adres odbiorcy.',
        subjectRequired: 'Podaj temat maila.',
      },
    },
    subgroups: {
      button: {
        manage: 'Wybierz dania',
      },
      dish: {
        delete: 'Usuń danie z podgrupy',
        hideDetails: 'Ukryj szczegóły',
        noDetails: 'Brak szczegółów do wyświetlenia.',
        showDetails: 'Pokaż szczegóły',
      },
      dishCategoryOptions: dishCategoryOptionsTranslation,
      distribute: {
        definition: 'Rozdziel porcje dla min. {{guests}} os. — najdroższe dania najpierw',
        definitionNoGuests: 'Ustaw min. liczbę osób w grupie, żeby rozdzielić porcje',
        definitionOutdated:
          'Ilości nie odpowiadają rozdziałowi dla min. {{guests}} os. — kliknij, aby rozdzielić (najdroższe dania najpierw)',
        label: 'Rozdziel',
        offer: 'Rozdziel porcje zgodnie z regułami podgrupy — najdroższe dania najpierw',
        offerBelow:
          'Rozdziel porcje zgodnie z regułami podgrupy — teraz {{allocation}}, poniżej budżetu',
        offerOver:
          'Rozdziel porcje zgodnie z regułami podgrupy — teraz {{allocation}}, ponad budżet',
        offerSet:
          'Rozdziel budżet między zaznaczone dania — najdroższe najpierw, każde co najmniej swoje minimum zamówienia; wybór się nie zmienia, odznaczone dania dostają 0',
        setBelowBudget:
          'Zaznaczone dania nie wypełniają budżetu podgrupy — kliknij „Rozdziel" albo zaznacz więcej dań',
        setBelowMinimum: '„{{dish}}" ma {{qty}} porcji, mniej niż minimum zamówienia ({{min}})',
        setMinimumsOverBudget:
          'Minima zamówienia zaznaczonych dań przekraczają budżet podgrupy — odznacz któreś z dań',
        setMoreIssues: 'Pozostałe dania do poprawy: {{count}}',
        setOverBudget:
          'Zaznaczone dania przekraczają budżet podgrupy — kliknij „Rozdziel" albo zmniejsz ilości',
        setStale: 'Rozdział porcji jest nieaktualny — kliknij „Rozdziel"',
        setUnselectedWithPortions: '„{{dish}}" jest odznaczone, a wciąż ma {{qty}} porcji',
        stationBelowBudget:
          'Ilości nie wypełniają budżetu podgrupy — kliknij „Rozdziel" albo dodaj dania',
        stationOverBudget:
          'Ilości przekraczają budżet podgrupy — kliknij „Rozdziel" albo zmniejsz ilości',
        stationStale: 'Ilości różnią się od rozdziału porcji — kliknij „Rozdziel"',
        unitMismatch:
          'Dania na sztuki nie mają gramatury, więc w tej podgrupie wypadają z rozdziału porcji ({{count}}): {{names}}',
      },
      editConfig: 'Edytuj konfigurację podgrupy (różnorodność, ilość na osobę)',
      mangeSubgroupByClicking: 'Kliknij, aby dodać dania',
      maxDistinct: 'Maks. różnych',
      maxDistinctDishes: 'Maks. do wyboru',
      maxPortions: 'Maks. porcji/os',
      menu: {
        deleteSubgroup: 'Usuń podgrupę',
        editSubgroup: 'Edytuj podgrupę',
      },
      modal: {
        delete: deleteModalTranslation,
      },
      rules: {
        below: 'poniżej budżetu',
        distinct: '{{selected}} z {{max}} dań',
        help: 'Podgrupa może mieć dwie reguły. „Różnych dań" mówi, ile pozycji trafia do oferty (przy limicie o składzie decyduje klient, więc liczymy tylko zaznaczone). „Ilość na osobę" to budżet gościa — w porcjach albo w gramach; przy gramaturze porcje przeliczamy z wagi porcji dania, a dania na sztuki zostają poza tym rozliczeniem. Budżet całej podgrupy to ta liczba razy liczba gości. „Rozdziel" zaczyna od najdroższych dań i każdemu wybranemu daje co najmniej jego minimum zamówienia. W zwykłej podgrupie bierze kolejne dania, dopóki starcza budżetu, a resztę odznacza — tak samo system rozkłada zestaw przy wstawianiu do oferty. W zestawie w ofercie dzieli budżet tylko między zaznaczone dania i niczego nie odznacza; sama zmiana wyboru porcji nie przelicza.',
        ok: 'zgodnie z regułami',
        over: 'ponad budżet',
        stale: 'rozdział porcji nieaktualny',
        unitMismatch: 'jednostka dania nie pasuje do podgrupy',
      },
      selectedCount: 'Zaznaczone dania: {{count}}',
      selectedOfMax: 'Wybrano {{count}} z {{max}}',
      set: {
        minPerItem: 'min {{min}}/danie',
        portionsHeader: 'Porcje',
        portionsValue: '{{count}} porcji',
      },
      setBadge: 'Zestaw',
      table: {
        summaryRow: 'Suma',
      },
    },
  },
  pdfPreview: {
    genericError: 'Nie udało się wygenerować podglądu.',
    legendTitle: 'Tryby podglądu PDF',
    offer: 'Oferta',
    offerDesc: 'Pełna oferta dla klienta ze wszystkimi daniami.',
    offerSelected: 'Wybrane',
    offerSelectedDesc: 'Oferta z daniami wybranymi przez klienta.',
    offerSelection: 'Do wyboru',
    offerSelectionDesc: 'Oferta dla klienta z polami wyboru i liczbą „do wyboru” na podgrupach.',
    realization: 'Realizacja',
    realizationDesc: 'Plik dla kuchni, cukierni etc. (w pakiecie: tylko zaznaczone).',
    scope: {
      all: 'Całość',
      allDesc: 'Wszystkie aktywne oferty z przeglądanej grupy ofert w jednym pliku.',
      current: 'Obecnie przeglądana',
      currentDesc: 'Tylko ta wersja oferty, którą teraz oglądasz.',
    },
    scopeLabel: 'Zakres',
    template: {
      label: 'Szablon wydruku',
      levelGroup: 'szablon całej grupy ofert',
      levelOffer: 'własny szablon tej oferty',
      missingDescription:
        'Wydruk oferty powstaje na okładce i tylnej stronie szablonu — bez niego nie ma z czego złożyć pliku. Wybierz szablon poniżej, żeby wygenerować podgląd.',
      missingTitle: 'Brak przypisanego szablonu wydruku',
      noTemplates:
        'Nie ma żadnego aktywnego szablonu. Dodaj go w widoku Oferty → Szablony wydruku.',
      save: 'Zapisz',
      saveAndGenerate: 'Zapisz i generuj',
    },
    title: 'Podgląd PDF',
    typeLabel: 'Typ wydruku',
  },
  processMessagesPreview: {
    button: {
      close: 'Zamknij',
      open: 'Maile i notatki procesu',
    },
    title: 'Maile i notatki procesu',
  },
  schedule: processOfferScheduleTranslation,
  sections: processOfferSectionsTranslation,
  sentLock: {
    aiChat:
      'Oferta jest wysłana, więc asystent nie może jej już zmieniać. Utwórz nową wersję, żeby ' +
      'dalej z nim pracować. Dotychczasową rozmowę możesz tu przeczytać.',
    checklist:
      'Oferta jest wysłana — kreator tylko pokazuje, co warto poprawić, a poprawki w treści ' +
      'wprowadzisz w nowej wersji. Kontakt realizacji i wyjaśnienia braku składowych uzupełnisz tutaj.',
    comment: {
      button: 'Komentarz dla realizacji',
      cancel: 'Anuluj',
      hint:
        'Komentarz trafia tylko do zlecenia realizacji, a nie do oferty dla klienta, więc zmienisz ' +
        'go także po wysłaniu.',
      save: 'Zapisz',
      title: 'Komentarz dla realizacji — {{name}}',
    },
    editDialog:
      'Oferta jest wysłana — tutaj zmienisz już tylko status, kontakt realizacji i szablon wydruku. ' +
      'Resztę zmienisz w nowej wersji.',
    goBack:
      'Do tej wersji zawsze możesz wrócić: otwórz ją i utwórz z niej kolejną wersję — dostaniesz ' +
      'szkic z dokładnie tą treścią.',
    howToChange:
      'Chcesz coś zmienić? Utwórz nową wersję. Powstanie wersja {{version}}: szkic z tą samą ' +
      'treścią, który możesz dowolnie edytować i wysłać klientowi.',
    newVersion: 'Nowa wersja',
    newVersionTooltip: 'Nowa wersja — ta oferta jest wysłana, zmiany wprowadzisz w nowej wersji',
    notLatest: 'To nie jest najnowsza wersja tej oferty.',
    openLatest: 'Otwórz najnowszą (wersja {{version}})',
    record: 'Ta wersja zostaje bez zmian — to zapis tego, co dostał klient.',
    sentAt: 'Wysłana {{date}}',
    sentTo: ' do: {{recipients}}',
    stillEditable:
      'Bez nowej wersji zmienisz tylko status, kontakt realizacji, harmonogram, komentarze dla ' +
      'realizacji, wyjaśnienia braku składowych i szablon wydruku.',
    title: 'Wersja {{version}} została wysłana klientowi — edycja jest zablokowana',
  },
  subgroup: {
    addDishHint: 'Kliknij, aby dodać dania',
    button: {
      addDishes: 'Dodaj dania spoza zestawu',
      cancel: 'Anuluj',
      finishManagingDishes: 'Zakończ zarządzanie',
      goBackToOffer: 'Zakończ edycję',
      manageDishes: 'Zarządzaj daniami',
      save: 'Zapisz',
      totalPrice: 'Suma podgrupy',
      unlimitedTotal: 'Bez limitu — w cenie zestawu',
    },
    content: {
      department: 'Departament',
      foodCost: 'FC',
      minimumSalePrice: 'Cena minimalna',
      orderMinimumPortions: 'Minimalne zamówienie',
      price: 'Cena',
      priceUnit: 'zł',
      quantity: 'Ilość',
      quantityUnit: 'szt',
      seasonality: 'Sezonowość',
      suggestedSalePrice: 'Cena sugerowana',
      totalPrice: 'Suma',
      type: 'Typ',
      unit: 'Gramatura',
    },
    dialog: {
      dishes: processOfferDishesTranslation,
    },
    dish: {
      delete: 'Usuń danie z podgrupy',
      departmentTitle: 'Dział: {{department}}',
      hideDetails: 'Ukryj szczegóły',
      minPrice: 'min {{price}}',
      minQuantity: 'min {{quantity}}',
      priceTitle: 'Cena · min {{min}} · sug {{suggested}} zł',
      quantityTitle: 'Ilość · min {{min}} porcji',
      showDetails: 'Pokaż szczegóły',
      totalPriceTitle: 'Suma · automatycznie (Ilość × Cena)',
      typeTitle: 'Typ: {{type}}',
    },
    selection: {
      belowMin: 'Poniżej minimum dania ({{min}} na zamówienie)',
      cost: 'Koszt wyboru: {{cost}} zł/os',
      costVsPackage: 'Koszt wyboru: {{cost}} zł/os · pakiet: {{pkg}} zł/os',
      counter: 'Wybrano {{count}} z {{max}}',
      distribute: 'Rozdziel porcje na {{guests}} gości',
      distributeTooltip:
        'Rozdziel budżet podgrupy ({{perGuest}}/os × {{guests}} gości) między zaznaczone dania — najdroższe najpierw, każde co najmniej swoje minimum zamówienia. Wybór się nie zmienia, odznaczone dania dostają 0; gdy minima nie mieszczą się w budżecie, każde danie dostaje swoje minimum.',
      distributeTooltipStation:
        'Rozdziel budżet podgrupy ({{perGuest}}/os × {{guests}} gości) — najdroższe dania najpierw, każde za swoje minimum zamówienia, dopóki starcza budżetu; pozostałe dania zostają odznaczone.',
      empty: 'Brak dań w puli do wyboru',
      gramsOfMax: 'Gramatura: {{total}} / maks {{max}} g ({{perGuest}} g/os × {{guests}} gości)',
      gramsRequired: 'Gramatura: {{total}} / min {{required}} g',
      minPortionsInfo: 'min {{min}} porcji',
      overDistinctWarning: '⚠ Wybrano {{selected}} różnych dań przy limicie {{max}}',
      perPortion: '{{price}} zł/porcja',
      portionsCount: 'Porcje: {{total}}',
      portionsOfMax: 'Porcje: {{total}} / maks {{max}} ({{perGuest}}/os × {{guests}} gości)',
      portionsRequired: 'Porcje: {{total}} / min {{required}}',
      quantityTitle: 'Ilość porcji z tej pozycji',
      unlimited: 'Bez limitu — w cenie zestawu, bez porcji',
      unselectedWithPortions: 'odznaczone, a ma {{qty}} porcji — kliknij „Rozdziel"',
    },
  },
};
